
import { formatPriceBRL } from "../../Utils/DataFormatters";
import { FaTimes } from "react-icons/fa";
import EfiPay from "payment-token-efi";
import Modal from "../Modal/Modal";

type PaymentOptionsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  bankingBilletPrice: number;
  pixPrice: number;
  creditCardInstallments: EfiPay.CreditCard.Installment[];
  isFetchingInstallments?: boolean;
  quantity?: number;
  maxNoInterestInstallments?: number;
  enableBankingBillet?: boolean;
  enablePix?: boolean;
  enableCreditCard?: boolean;
  creditCardSurchargePercent?: number;
};

const PaymentOptionsModal = ({
  isOpen,
  onClose,
  bankingBilletPrice,
  pixPrice,
  creditCardInstallments,
  isFetchingInstallments = false,
  quantity = 1,
  maxNoInterestInstallments = 0,
  enableBankingBillet = true,
  enablePix = true,
  enableCreditCard = true,
  creditCardSurchargePercent = 0
}: PaymentOptionsModalProps) => {

  if (!isOpen) {
    return null;
  }

  const multiplier = Math.max(1, quantity);
  const billetTotal = (bankingBilletPrice || 0) * multiplier;
  const pixTotal = (pixPrice || 0) * multiplier;
  const surchargeMultiplier = creditCardSurchargePercent > 0 ? 1 + creditCardSurchargePercent / 100 : 1;
  const baseCardTotal = (pixTotal || billetTotal) * surchargeMultiplier;
  const maxNoInterest = Math.max(0, maxNoInterestInstallments || 0);

  return (
    <Modal overlayClassName="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-md max-w-xs md:max-w-sm w-full px-6 py-8 relative">
        <button
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
          onClick={onClose}
        >
          <FaTimes />
        </button>
        <h2 className="text-md font-bold mb-4 text-center">{"Formas de pagamento"}</h2>

        {enableBankingBillet && (
          <div className="mb-4 text-center text-sm md:text-md">
            <h3 className="font-medium mb-1">{"Boleto bancário"}</h3>
            <div className="border border-gray-300 border-t-2 border-t-primary-600 p-2 font-semibold">
              {formatPriceBRL(billetTotal)}
            </div>
          </div>
        )}

        {enablePix && pixPrice !== undefined && (
          <div className="mb-4 text-center text-sm md:text-md">
            <h3 className="font-medium mb-1">PIX</h3>
            <div className="border border-gray-300 border-t-2 border-t-primary-600 p-2 font-semibold">
              {formatPriceBRL(pixTotal)}
            </div>
          </div>
        )}

        {enableCreditCard && (
        <div className="text-center text-sm md:text-md">
          <h3 className="font-medium mb-1">{"Cartão de crédito"}</h3>
          <div className="border border-gray-300 border-t-2 border-t-primary-600 p-2">
            {isFetchingInstallments ? (
              <div className="text-xs md:text-sm text-gray-600">{"Carregando parcelas..."}</div>
            ) : creditCardInstallments && creditCardInstallments.length > 0 ? (
              creditCardInstallments.map((installment) => {
                const installments = installment.installment;
                // EFI returns `value` as the per-installment amount in cents; apply surcharge
                const gatewayPerInstallment = ((installment.value || 0) / 100) * multiplier * surchargeMultiplier;
                const storeAbsorbs = maxNoInterest > 0 && installments <= maxNoInterest;
                // When store absorbs interest and a base price is available, split evenly;
                // otherwise use EFI's per-installment value directly (already correct in both cases)
                const pricePerInstallment = storeAbsorbs && baseCardTotal
                  ? baseCardTotal / installments
                  : gatewayPerInstallment;
                // 1x always sem juros — customer pays no extra regardless of settings
                const suffix = storeAbsorbs || installments === 1 ? "sem juros" : "com juros";

                return (
                  <div key={installments} className="text-xs md:text-sm mb-2">
                    {installments}x {"de"}{" "}
                    <span className="font-semibold">
                      {formatPriceBRL(pricePerInstallment)}
                    </span>{" "}
                    {suffix}
                  </div>
                );
              })
            ) : (
              <div className="text-xs md:text-sm text-gray-600">{"Parcelas não disponíveis no momento."}</div>
            )}
          </div>
        </div>
        )}
      </div>
    </Modal>
  );
};

export default PaymentOptionsModal;
